import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import { getLocation } from '@change-corgi/core/window';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { FacebookPost, RedditPost } from 'src/app/pages/starterDashboardV2/shared/components';
import { useFlyerDownloadModalContext } from 'src/app/pages/starterDashboardV2/shared/components';
import { useSharingModal } from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { RecommendedGroup } from 'src/app/pages/starterDashboardV2/shared/types';
import { useIsMobile } from 'src/app/shared/hooks/userAgent';

import {
	type UseSharingToolsReturn as BaseUseSharingToolsReturn,
	useSharingTools as useBaseSharingTools,
} from './index';

type SharingToolsActions = {
	navigateToAllTools: () => void;
	navigateToGroupRecommendations: () => void;
	navigateToReadyToSharePosts: () => void;
	navigateToShareVisual: () => void;
	openFlyerDownloadModal: () => void;
	navigateToEditPetition: () => void;
	openRTSFacebookSharingModal: () => void;
	openRTSInstagramSharingModal: () => void;
	openRecommendedFacebookGroupModal: (group: RecommendedGroup) => void;
	openRecommendedRedditGroupModal: (group: RecommendedGroup) => void;
};

type UseSharingToolsReturn = Omit<BaseUseSharingToolsReturn, 'actions'> & { actions: SharingToolsActions };

const RTS_TRACKING_PROPS = {
	context: 'petition_dashboard_grow_petition_v2',
	viewContext: 'home',
} as const;

const RG_TRACKING_PROPS = {
	context: 'petition_dashboard_grow_petition_v2',
	viewContext: 'home',
} as const;

const CLICK_RTS_EVENT_NAME = 'petition_manage_ready_to_share_posts_click';
const CLICK_RG_EVENT_NAME = 'petition_manage_recommended_groups_click';
const CONTEXT = 'petition_dashboard_grow_petition_v2';
const VIEW_CONTEXT = 'home';

function trackRTSFacebookClick(track: ReturnType<typeof useTracking>) {
	void track(CLICK_RTS_EVENT_NAME, {
		context: CONTEXT,
		view_context: VIEW_CONTEXT,
		location: 'facebook_post',
	});
}

function trackRTSInstagramClick(track: ReturnType<typeof useTracking>) {
	void track(CLICK_RTS_EVENT_NAME, {
		context: CONTEXT,
		view_context: VIEW_CONTEXT,
		location: 'instagram_story',
	});
}

function trackRecommendedGroupClick(
	track: ReturnType<typeof useTracking>,
	groupName: string,
	platform: 'facebook' | 'reddit',
	groupSource: string,
) {
	const sourceKey = platform === 'facebook' ? 'fb_recommendation_source' : 'reddit_recommendation_source';
	void track(CLICK_RG_EVENT_NAME, {
		context: CONTEXT,
		view_context: VIEW_CONTEXT,
		location: `${platform}_group`,
		recommendation: groupName,
		[sourceKey]: groupSource,
	});
}

function navigateToRTSInstagramPosts(slug: string, navigate: ReturnType<typeof useNavigate>): void {
	const location = getLocation();
	const searchParams = new URLSearchParams(location.search);
	searchParams.set('platform', 'instagram');
	navigate(`/p/${slug}/dashboard/share?${searchParams.toString()}#ready-to-share-posts`);
}

// eslint-disable-next-line max-lines-per-function
export function useSharingTools(): UseSharingToolsReturn {
	const { data, actions } = useBaseSharingTools('petition_dashboard_grow_petition_v2');
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const { openModal: openFlyerDownloadModal } = useFlyerDownloadModalContext();
	const { startSharingFlow } = useSharingModal();
	const isMobile = useIsMobile();
	const navigate = useNavigate();
	const openRecommendedFacebookGroupModal = (group: RecommendedGroup) => {
		const props: Omit<FacebookPost, 'isOpen'> = {
			platform: 'facebook',
			postType: 'post',
			petition,
			recommendedGroup: group,
			defaultOpen: false,
			trackingProps: RG_TRACKING_PROPS,
		};
		trackRecommendedGroupClick(track, group.name, 'facebook', group.metadata.source);
		startSharingFlow(props);
	};

	const openRecommendedRedditGroupModal = (group: RecommendedGroup) => {
		const props: Omit<RedditPost, 'isOpen'> = {
			platform: 'reddit',
			postType: 'post',
			petition,
			recommendedGroup: group,
			defaultOpen: false,
			trackingProps: RG_TRACKING_PROPS,
		};
		trackRecommendedGroupClick(track, group.name, 'reddit', group.metadata.source);
		startSharingFlow(props);
	};

	const track = useTracking();
	return {
		data,
		actions: {
			...actions,
			openFlyerDownloadModal: () => {
				void track('petition_manage_grow_your_petition_click', {
					context: 'petition_dashboard_grow_petition_v2',
					content: 'poster',
					cta: 'button',
				});
				openFlyerDownloadModal('starter_dashboard_sharing_tools');
			},
			openRTSFacebookSharingModal: () => {
				trackRTSFacebookClick(track);
				startSharingFlow({
					platform: 'facebook',
					postType: 'post',
					petition,
					defaultOpen: false,
					trackingProps: RTS_TRACKING_PROPS,
				});
			},
			openRTSInstagramSharingModal: () => {
				trackRTSInstagramClick(track);
				if (isMobile) {
					startSharingFlow({
						platform: 'instagram',
						postType: 'story',
						petition,
						defaultOpen: false,
						trackingProps: RTS_TRACKING_PROPS,
					});
				} else {
					navigateToRTSInstagramPosts(petition.slug, navigate);
				}
			},
			openRecommendedFacebookGroupModal,
			openRecommendedRedditGroupModal,
		},
	};
}
